import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Bell, BellOff, Smartphone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const PushNotificationSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    checkPushSupport();
    loadSettings();
  }, []);

  const checkPushSupport = () => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    setSupported(isSupported);
    if (isSupported && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get('/notifications/push/status');
      setEnabled(res.data.enabled);
    } catch (error) {
      console.error('Error loading push settings');
    } finally {
      setLoading(false);
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Tu navegador no soporta notificaciones');
      return false;
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    return result === 'granted';
  };

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const vapidRes = await api.get('/notifications/push/vapid-key');
      const vapidPublicKey = vapidRes.data.public_key;

      if (!vapidPublicKey) {
        toast.error('Las notificaciones push no están disponibles en este momento');
        return { error: true };
      }

      // Convert VAPID key
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    }
  };

  const handleToggle = async (newValue) => {
    if (!supported) {
      toast.error('Tu navegador no soporta notificaciones push');
      return;
    }

    setUpdating(true);

    try {
      if (newValue) {
        // Enable push
        const hasPermission = await requestPermission();
        if (!hasPermission) {
          toast.error('Necesitas permitir las notificaciones');
          setUpdating(false);
          return;
        }

        const subscription = await subscribeToPush();
        if (subscription && !subscription.error) {
          await api.post('/notifications/push/subscribe', {
            subscription: subscription.toJSON()
          });
          setEnabled(true);
          toast.success('Notificaciones push activadas');
        } else if (!subscription) {
          toast.error('Error al activar notificaciones. Inténtalo de nuevo.');
        }
        // If subscription.error, subscribeToPush already showed a toast
      } else {
        // Disable push
        await api.post('/notifications/push/unsubscribe');
        setEnabled(false);
        toast.success('Notificaciones push desactivadas');
      }
    } catch (error) {
      toast.error('Error al cambiar configuración');
    } finally {
      setUpdating(false);
    }
  };

  // Helper to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (loading) {
    return (
      <Card className="border-border rounded-sm shadow-none">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border rounded-sm shadow-none" data-testid="push-notifications-card">
      <CardHeader>
        <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Bell size={18} className="text-primary" />
          Notificaciones Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-sm border border-border">
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-10 h-10 rounded-sm flex items-center justify-center",
              enabled ? "bg-primary/10" : "bg-muted"
            )}>
              {enabled ? (
                <Bell size={20} className="text-primary" />
              ) : (
                <BellOff size={20} className="text-muted-foreground" />
              )}
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">
                {enabled ? 'Notificaciones Activas' : 'Notificaciones Desactivadas'}
              </p>
              <p className="text-xs text-muted-foreground">
                {enabled 
                  ? 'Recibirás alertas de presupuestos, transacciones recurrentes y más'
                  : 'Activa para recibir alertas importantes'}
              </p>
            </div>
          </div>
          
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={updating || !supported}
            data-testid="push-notifications-toggle"
          />
        </div>

        {!supported && (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-sm">
            <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <Smartphone size={14} />
              Tu navegador no soporta notificaciones push
            </p>
          </div>
        )}

        {supported && permission === 'denied' && (
          <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-sm">
            <p className="text-xs text-red-700 dark:text-red-400">
              Has bloqueado las notificaciones. Para activarlas, cambia los permisos en la configuración del navegador.
            </p>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p className="font-medium mb-1">Tipos de notificaciones:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Alertas de presupuesto (80% y 100%)</li>
            <li>Transacciones recurrentes ejecutadas</li>
            <li>Alertas de seguridad</li>
            <li>Resumen semanal</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
